import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Mail, Phone, Calendar, Reply, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ListToolbar, Pagination } from '../components/ListToolbar';
import {
  adminGetQuoteRequests, adminUpdateQuoteRequestStatus, adminExportQuotes,
  adminSendEmail,
} from '../api/api';

const PAR_PAGE = 25;
const STATUTS = [
  { valeur: 'new', libelle: 'Nouvelles' },
  { valeur: 'contacted', libelle: 'Contactées' },
  { valeur: 'completed', libelle: 'Traitées' },
];
const LIBELLE_STATUT = { new: 'nouvelle', contacted: 'contactée', completed: 'traitée' };

// Message pré-rempli : une base polie et professionnelle que l'exploitant
// complète avant l'envoi. Le prénom et le service demandé sont repris pour
// personnaliser d'emblée la réponse.
const messageParDefaut = (quote) => {
  const prenom = (quote.name || '').trim().split(' ')[0] || 'Madame, Monsieur';
  const service = quote.service ? ` pour votre projet de « ${quote.service} »` : '';
  return `Bonjour ${prenom},

Nous vous remercions de votre demande de devis${service} et de l'intérêt que vous portez à NETTO+.

Voici notre proposition :

• Prestation : ${quote.service || 'à préciser'}
• Tarif : … € (TVAC)
• Délai d'intervention : sous … jours

Ce devis est gratuit et sans engagement. Nous restons à votre entière disposition pour toute précision ou pour convenir d'un rendez-vous.

Dans l'attente de votre retour, nous vous adressons nos salutations les meilleures.`;
};

// ---------------------------------------------------- Fenêtre de réponse
const FenetreReponse = ({ quote, onClose, onEnvoye }) => {
  const [sujet, setSujet] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (quote) {
      setSujet('Votre demande de devis – NETTO+');
      setMessage(messageParDefaut(quote));
    }
  }, [quote]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!sujet.trim() || !message.trim()) {
      toast.error('Objet et message sont requis.');
      return;
    }
    setEnvoi(true);
    try {
      await adminSendEmail({ to: quote.email, subject: sujet, message });
      // Une réponse envoyée signifie que le client a été contacté : on fait
      // avancer le statut automatiquement (sans écraser un devis déjà traité).
      if (quote.status === 'new') {
        try {
          await adminUpdateQuoteRequestStatus(quote.id, 'contacted');
          onEnvoye(quote.id, 'contacted');
        } catch {
          onEnvoye(quote.id, quote.status);
        }
      } else {
        onEnvoye(quote.id, quote.status);
      }
      toast.success('Réponse envoyée.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'envoi a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Répondre à {quote?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dest">Destinataire</Label>
            <Input id="dest" value={quote?.email || ''} disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sujet">Objet</Label>
            <Input
              id="sujet"
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="corps">Message</Label>
            <Textarea
              id="corps"
              rows={10}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>
          <p className="text-xs text-gray-500">
            Envoyé depuis contact@nettoplus.be. Votre signature (nom, rôle, logo) est ajoutée automatiquement, et les réponses du client reviennent sur contact@nettoplus.be.
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={envoi} className="gap-2">
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {envoi ? 'Envoi…' : 'Envoyer la réponse'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [loading, setLoading] = useState(true);
  const [repondre, setRepondre] = useState(null);

  // La recherche est temporisée : une requête par frappe saturerait l'API.
  useEffect(() => {
    const minuteur = setTimeout(() => {
      setLoading(true);
      adminGetQuoteRequests({
        skip,
        limit: PAR_PAGE,
        ...(recherche ? { search: recherche } : {}),
        ...(statut ? { status: statut } : {}),
      })
        .then((data) => {
          setQuotes(data.items);
          setTotal(data.total);
        })
        .catch(() => toast.error('Erreur lors du chargement'))
        .finally(() => setLoading(false));
    }, recherche ? 350 : 0);
    return () => clearTimeout(minuteur);
  }, [skip, recherche, statut]);

  // Tout changement de filtre ramène à la première page.
  useEffect(() => {
    setSkip(0);
  }, [recherche, statut]);

  const majStatutLocal = (id, status) => {
    setQuotes((liste) => liste.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateQuoteRequestStatus(id, status);
      toast.success('Statut mis à jour');
      majStatutLocal(id, status);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || colors.new;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Demandes de devis</h2>
        <p className="text-gray-600">Suivez, répondez et qualifiez chaque demande reçue.</p>
      </div>

      <ListToolbar
        recherche={recherche}
        onRecherche={setRecherche}
        statut={statut}
        onStatut={setStatut}
        statuts={STATUTS}
        onExport={adminExportQuotes}
        total={total}
        libelleTotal="demande"
      />

      {loading && (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && quotes.length === 0 && (
        <Card className="p-12 text-center text-gray-500">
          {recherche || statut
            ? 'Aucune demande ne correspond à ces critères.'
            : 'Aucune demande pour le moment.'}
        </Card>
      )}

      <div className="space-y-4">
        {!loading && quotes.map((quote) => (
          <Card key={quote.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{quote.name}</h3>
                  <Badge className={getStatusColor(quote.status)}>
                    {LIBELLE_STATUT[quote.status] || quote.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${quote.email}`} className="hover:text-blue-600">{quote.email}</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${quote.phone}`} className="hover:text-blue-600">{quote.phone}</a>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-sm font-semibold text-gray-700">Service:</span>
                  <span className="text-sm text-gray-600 ml-2">{quote.service}</span>
                </div>

                {quote.message && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-700">{quote.message}</p>
                  </div>
                )}

                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(quote.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              <div className="flex flex-col space-y-2 ml-4">
                <Button
                  size="sm"
                  onClick={() => setRepondre(quote)}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  <Reply className="w-4 h-4" />
                  Répondre
                </Button>
                {quote.status === 'new' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(quote.id, 'contacted')}
                  >
                    Marquer contacté
                  </Button>
                )}
                {quote.status === 'contacted' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(quote.id, 'completed')}
                  >
                    Marquer complété
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Pagination skip={skip} limit={PAR_PAGE} total={total} onChange={setSkip} />

      <FenetreReponse
        quote={repondre}
        onClose={() => setRepondre(null)}
        onEnvoye={majStatutLocal}
      />
    </div>
  );
};
